import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { switchMap, take, filter, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
    private refreshTokenInProgress = false;
    private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);

    constructor(public authService: AuthService, private router: Router) { }
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // #80: the SSO token exchange is an anonymous auth-bootstrap call, like /token
        // and /refresh — it must skip the inject-or-refresh path. Otherwise a re-login
        // with an expired access + still-valid refresh would fire a /token refresh
        // (or log the user out) mid-exchange instead of letting the code redeem.
        //
        // /api/app-version/ is here for a sharper reason: it is [AllowAnonymous] and
        // it is called unconditionally at app start, before anyone has decided to log
        // in. Without this skip, launching the app with an expired access token and a
        // live refresh token takes the refresh branch below on that bootstrap call —
        // so a transient network failure at launch calls logout(), which clears
        // localStorage (taking the in-flight SSO PKCE verifier with it) and bounces
        // the user to the login screen. A version check must never be able to log
        // anyone out.
        if (request.url.indexOf('/refresh') !== -1
            || request.url.indexOf('/token') !== -1
            || request.url.indexOf('/api/sso/') !== -1
            || request.url.indexOf('/api/app-version/') !== -1)  {
            return next.handle(request);
        }

        const accessExpired = this.authService.isAccessTokenExpired();
        const refreshExpired = this.authService.isRefreshTokenExpired();

        if (accessExpired && refreshExpired) {
            return next.handle(this.injectToken(request));
        }
        if (accessExpired && !refreshExpired) {
            if (!this.refreshTokenInProgress) {
                this.refreshTokenInProgress = true;
                this.refreshTokenSubject.next(null);
                return this.authService.refreshAccessToken().pipe(
                    switchMap((authResponse: any) => {
                        this.authService.storeToken(authResponse.access_token);
                        this.authService.storeRefreshToken(authResponse.refresh_token);
                        this.refreshTokenInProgress = false;
                        this.refreshTokenSubject.next(authResponse.refreshToken);
                        return next.handle(this.injectToken(request));
                    }),
                    catchError((e) => {
                        this.authService.logout();
                        this.router.navigateByUrl('/auth/login');
                        return next.handle(request);
                    })
                );
            } else {
                return this.refreshTokenSubject.pipe(
                    filter(result => result !== null),
                    take(1),
                    switchMap((res) => {
                        return next.handle(this.injectToken(request));
                    })
                );
            }
        }

        if (!accessExpired) {
            return next.handle(this.injectToken(request));
        }
    }

    injectToken(request: HttpRequest<any>) {
        const token = this.authService.getToken();
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

}
